import React from 'react';
import { Shield, Lock, Database, Server, Users, FileCheck } from 'lucide-react';
import { SEO } from '../components/SEO';

export function Security() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Security & Compliance | Doyen AI"
        description="Enterprise-grade security measures ensuring your financial data stays protected"
        url={`${window.location.origin}/security`}
      />

      {/* Header Section */}
      <section className="relative bg-card pt-32 pb-24">
        {/* Grid Pattern Background */}
        <div className="absolute inset-0 grid-pattern opacity-30" />
        
        {/* Content */}
        <div className="relative text-center">
          <h1 className="text-5xl font-bold text-primary mb-6">Protecting Your Financial Data</h1>
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto px-4">
            We understand the critical importance of financial data security. Our platform meets the highest industry standards, ensuring your sensitive information is protected with the same level of security trusted by leading financial institutions.
          </p>
        </div>
      </section>

      {/* Security Features Grid */}
      <section className="bg-[#F5F5F5] pt-24 pb-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "SOC2 Certified",
                description: "Our platform meets rigorous SOC2 standards, the gold standard for financial data security.",
                icon: Shield
              },
              {
                title: "Complete Data Separation",
                description: "Your financial data remains completely separate from other organizations, ensuring privacy.",
                icon: Users
              },
              {
                title: "Bank-Grade Encryption",
                description: "We use the same encryption standards trusted by leading financial institutions.",
                icon: Database
              },
              {
                title: "Secure Data Transfer",
                description: "All data transfers are protected with the latest security protocols.",
                icon: Lock
              },
              {
                title: "Access Management",
                description: "Control exactly who can access your financial data with customizable permissions.",
                icon: Server
              },
              {
                title: "Compliance Ready",
                description: "Built to meet financial industry regulations and compliance requirements.",
                icon: FileCheck
              }
            ].map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-[#E5E5E5]">
                <feature.icon className="w-12 h-12 text-[#8000FF] mb-4" />
                <h3 className="text-xl font-semibold mb-2 text-[#1A1A1A]">{feature.title}</h3>
                <p className="text-[#525252]">{feature.description}</p>
              </div>
            ))}
          </div>
          
          {/* SOC2 Logo */}
          <div className="flex justify-center mt-8">
            <img 
              src="/website-images/soc2_logo.png"
              alt="SOC2 Certified"
              className="h-80 w-auto"
            />
          </div>
        </div>
      </section>

      {/* Security Commitment Section */}
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-[#1A1A1A] mb-4">Our Commitment to Your Security</h2>
            <p className="text-lg text-[#525252] max-w-3xl mx-auto">
              As financial professionals, you need absolute confidence in your data security. Our platform is built with multiple layers of protection to ensure your sensitive information remains safe and compliant.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-[#F5F5F5] rounded-xl p-8 shadow-sm border border-[#E5E5E5]">
              <h3 className="text-xl font-semibold text-[#1A1A1A] mb-4">Data Privacy Safeguards</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <Shield className="w-6 h-6 text-[#8000FF] mr-3 flex-shrink-0 mt-1" />
                  <p className="text-[#525252]">Private cloud environment for your organization's data</p>
                </li>
                <li className="flex items-start">
                  <Lock className="w-6 h-6 text-[#8000FF] mr-3 flex-shrink-0 mt-1" />
                  <p className="text-[#525252]">Industry-leading encryption for all stored information</p>
                </li>
                <li className="flex items-start">
                  <Users className="w-6 h-6 text-[#8000FF] mr-3 flex-shrink-0 mt-1" />
                  <p className="text-[#525252]">Detailed access controls and audit trails for accountability</p>
                </li>
              </ul>
            </div>

            <div className="bg-[#F5F5F5] rounded-xl p-8 shadow-sm border border-[#E5E5E5]">
              <h3 className="text-xl font-semibold text-[#1A1A1A] mb-4">Continuous Protection</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <Database className="w-6 h-6 text-[#8000FF] mr-3 flex-shrink-0 mt-1" />
                  <p className="text-[#525252]">Regular third-party security assessments</p>
                </li>
                <li className="flex items-start">
                  <Server className="w-6 h-6 text-[#8000FF] mr-3 flex-shrink-0 mt-1" />
                  <p className="text-[#525252]">24/7 security monitoring and threat prevention</p>
                </li>
                <li className="flex items-start">
                  <FileCheck className="w-6 h-6 text-[#8000FF] mr-3 flex-shrink-0 mt-1" />
                  <p className="text-[#525252]">Automatic backups and business continuity measures</p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-[#F5F5F5] py-24">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#1A1A1A] text-center mb-12">Common Security Questions</h2>
          <div className="space-y-8">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E5E5E5]">
              <h3 className="text-xl font-semibold text-[#1A1A1A] mb-3">How do you protect our financial data?</h3>
              <p className="text-[#525252]">
                Your financial data is stored in a dedicated environment with bank-grade encryption, ensuring complete separation from other organizations' data.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E5E5E5]">
              <h3 className="text-xl font-semibold text-[#1A1A1A] mb-3">Is our data used for any other purposes?</h3>
              <p className="text-[#525252]">
                No. Your financial data is used solely for your migration project. We never use customer data for AI training or any other purposes.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E5E5E5]">
              <h3 className="text-xl font-semibold text-[#1A1A1A] mb-3">How do you handle sensitive financial information?</h3>
              <p className="text-[#525252]">
                All sensitive data stays within your dedicated environment. We never share or expose your financial information to external systems or third parties.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E5E5E5]">
              <h3 className="text-xl font-semibold text-[#1A1A1A] mb-3">What security measures protect our data?</h3>
              <p className="text-[#525252]">
                We use multiple layers of security including encryption, access controls, and continuous monitoring to protect your financial information at every step.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E5E5E5]">
              <h3 className="text-xl font-semibold text-[#1A1A1A] mb-3">What compliance standards do you meet?</h3>
              <p className="text-[#525252]">
                Our platform is SOC2 certified and designed to meet the strict security requirements of financial institutions and regulatory bodies.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-white py-24">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-br from-[#8000FF] to-[#6600CC] text-white py-24 rounded-xl">
            <div className="max-w-3xl mx-auto px-4 text-center">
              <h2 className="text-3xl font-bold mb-6">Ready to Discuss Your Security Requirements?</h2>
              <p className="text-xl mb-8">
                Our security team is available to answer your questions about how we protect your financial data.
              </p>
              <a 
                href="mailto:security@doyen.com" 
                className="inline-flex items-center px-6 py-3 border-2 border-white rounded-lg text-lg font-semibold hover:bg-white hover:text-[#8000FF] transition-colors"
              >
                Contact Security Team
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}